/**
 * WebGPU IO-binding flow sampling loop.
 *
 * Keeps `latent` and `camera` resident on the GPU across all flow steps.
 * Per step that saves:
 *
 *   - the upload of `latent` (~256 KB fp16 / 512 KB fp32) and `camera`
 *   - the readback of both `latent_out` and `camera_out`
 *   - the JS allocations + loops in stepEuler and cfgBlend
 *
 * With 20 steps + CFG (40 flow runs) that's ~80 round trips and ~80 JS-side
 * 130k-element loops gone. The Euler-plus-CFG update happens in a tiny WGSL
 * compute shader on the same device, so it serializes naturally with ORT's
 * own GPU queue submissions.
 *
 * The cond tensors (feat1, feat2) and their negatives are also uploaded
 * once at init -- they don't change across steps, so without IO binding
 * ORT was re-binding the same CPU-side Tensor to GPU every call.
 *
 * Layout matches the model's expected dtype, picked from
 * `flow.inputMetadata.find("latent").type`:
 *
 *   - fp32 flow -> array<f32> buffers + plain Euler kernel.
 *   - fp16 flow -> array<u32> buffers (two fp16 per u32), kernel uses
 *     pack2x16float / unpack2x16float so no `shader-f16` device feature
 *     is required. Works on every WebGPU adapter.
 */
import * as ort from "onnxruntime-web";
import type { InferenceSession } from "onnxruntime-web";
import { fp32ToFp16, fp16ToFp32 } from "./models";

const D = 16; // latent channels (model.in_channels)
const C = 5;  // camera channels (model.cam_channels)
const WG_SIZE = 64;

export interface GpuLoopArgs {
  device: GPUDevice;
  flow: InferenceSession;
  L: number;                 // latent token count (8192 in the default export)
  condL: number;             // cond token count for feat1 / feat2 (4101)
  feat1Ch: number;           // 1280 for DINOv3
  feat2Ch: number;           // 128 for Flux2 VAE encoder
  feat1: Float32Array;       // DINOv3 features, length 1*condL*feat1Ch
  feat2: Float32Array;       // VAE features (zero-prefixed), length 1*condL*feat2Ch
  initLatent: Float32Array;  // length 1*L*D
  initCamera: Float32Array;  // length 1*1*C
  useCfg: boolean;
}

const PARAMS_BYTES = 16; // dt, s, cfg, _pad -- 4 f32

export class GpuFlowLoop {
  private dev!: GPUDevice;
  private flow!: InferenceSession;
  private isFp16!: boolean;
  private L!: number;

  // Persistent buffers (one allocation per generation).
  private latentBuf!: GPUBuffer;
  private cameraBuf!: GPUBuffer;
  private feat1Buf!: GPUBuffer;
  private feat2Buf!: GPUBuffer;
  private negFeat1Buf!: GPUBuffer;
  private negFeat2Buf!: GPUBuffer;
  private condLatentOutBuf!: GPUBuffer;
  private condCameraOutBuf!: GPUBuffer;
  private uncondLatentOutBuf: GPUBuffer | null = null;
  private uncondCameraOutBuf: GPUBuffer | null = null;
  private paramsBuf!: GPUBuffer;

  // ORT tensors that wrap the buffers above. Reused across all step() calls.
  private latentT!: ort.Tensor;
  private cameraT!: ort.Tensor;
  private feat1T!: ort.Tensor;
  private feat2T!: ort.Tensor;
  private negFeat1T!: ort.Tensor;
  private negFeat2T!: ort.Tensor;
  private condFetches!: Record<string, ort.Tensor>;
  private uncondFetches: Record<string, ort.Tensor> | null = null;

  private pipeline!: GPUComputePipeline;
  // Bind groups for the Euler kernel, one per (target, cfg-on/off) combo.
  private latentBindOff!: GPUBindGroup;
  private cameraBindOff!: GPUBindGroup;
  private latentBindOn: GPUBindGroup | null = null;
  private cameraBindOn: GPUBindGroup | null = null;

  private destroyed = false;
  private stepCount = 0;

  /** Detect whether the loaded flow session wants fp16 or fp32 on the
   *  `latent` input. The model's other input/output dtypes follow. */
  static flowDtype(flow: InferenceSession): "float16" | "float32" | null {
    const meta = flow.inputMetadata.find(
      (m): m is ort.InferenceSession.TensorValueMetadata =>
        m.isTensor && m.name === "latent");
    if (!meta) return null;
    if (meta.type === "float16" || meta.type === "float32") return meta.type;
    return null;
  }

  async init(a: GpuLoopArgs) {
    // Always allocate buffers against ORT's current WebGPU device, not
    // whatever was cached at boot. If ORT swapped its internal device for
    // the flow session, `(ort.env.webgpu as any).device` reflects the
    // active one. Buffer-device mismatch is the source of the
    // "[Buffer X] is associated with [Device], and cannot be used with
    // [Device]" rejection we were getting from session.run.
    const ortDev = (ort.env.webgpu as any)?.device as GPUDevice | undefined;
    this.dev = ortDev ?? a.device;
    this.flow = a.flow;
    this.L = a.L;
    const dt = GpuFlowLoop.flowDtype(a.flow);
    if (!dt) throw new Error("flow model has no float32/float16 `latent` input");
    this.isFp16 = dt === "float16";

    const elBytes = this.isFp16 ? 2 : 4;
    // ORT-Web pads its WebGPU tensor outputs to 16-byte alignment when it
    // writes them back into our preallocated fetches buffers. If the
    // logical tensor size (e.g. 5 fp32 = 20 bytes for the camera output)
    // isn't a multiple of 16, ORT's copyBufferToBuffer overruns ours --
    // the validation error reads "Copy range (size: 32) does not fit in
    // [Buffer "cCOut"] size (20)". Round every buffer up to 16 bytes.
    const ceil16 = (n: number) => Math.ceil(n / 16) * 16;
    const latentBytes  = ceil16(a.L * D * elBytes);
    const cameraBytes  = ceil16(C * elBytes);
    const feat1Bytes   = ceil16(a.condL * a.feat1Ch * elBytes);
    const feat2Bytes   = ceil16(a.condL * a.feat2Ch * elBytes);

    const STORAGE = GPUBufferUsage.STORAGE;
    const COPY_SRC = GPUBufferUsage.COPY_SRC;
    const COPY_DST = GPUBufferUsage.COPY_DST;
    const UNIFORM = GPUBufferUsage.UNIFORM;

    this.latentBuf = this.dev.createBuffer({
      label: "latent",   size: latentBytes, usage: STORAGE | COPY_SRC | COPY_DST });
    this.cameraBuf = this.dev.createBuffer({
      label: "camera",   size: cameraBytes, usage: STORAGE | COPY_SRC | COPY_DST });
    this.feat1Buf = this.dev.createBuffer({
      label: "feat1",    size: feat1Bytes,  usage: STORAGE | COPY_DST });
    this.feat2Buf = this.dev.createBuffer({
      label: "feat2",    size: feat2Bytes,  usage: STORAGE | COPY_DST });
    // Zero-init is guaranteed by the WebGPU spec; no explicit clear needed.
    this.negFeat1Buf = this.dev.createBuffer({
      label: "negFeat1", size: feat1Bytes,  usage: STORAGE });
    this.negFeat2Buf = this.dev.createBuffer({
      label: "negFeat2", size: feat2Bytes,  usage: STORAGE });
    // Output buffers: ORT writes its session results into these via
    // copyBufferToBuffer, so they need COPY_DST too -- without it the EP
    // rejects the write with
    //   [Buffer "cCOut"] usage doesn't include BufferUsage::CopyDst.
    this.condLatentOutBuf = this.dev.createBuffer({
      label: "cLOut",    size: latentBytes, usage: STORAGE | COPY_SRC | COPY_DST });
    this.condCameraOutBuf = this.dev.createBuffer({
      label: "cCOut",    size: cameraBytes, usage: STORAGE | COPY_SRC | COPY_DST });
    if (a.useCfg) {
      this.uncondLatentOutBuf = this.dev.createBuffer({
        label: "uLOut",  size: latentBytes, usage: STORAGE | COPY_SRC | COPY_DST });
      this.uncondCameraOutBuf = this.dev.createBuffer({
        label: "uCOut",  size: cameraBytes, usage: STORAGE | COPY_SRC | COPY_DST });
    }
    this.paramsBuf = this.dev.createBuffer({
      label: "params",   size: PARAMS_BYTES, usage: UNIFORM | COPY_DST });

    this.writeBuf(this.latentBuf, a.initLatent, latentBytes);
    this.writeBuf(this.cameraBuf, a.initCamera, cameraBytes);
    this.writeBuf(this.feat1Buf,  a.feat1,      feat1Bytes);
    this.writeBuf(this.feat2Buf,  a.feat2,      feat2Bytes);

    const dtype = this.isFp16 ? "float16" : "float32";
    const wrap = (buf: GPUBuffer, dims: readonly number[]) =>
      ort.Tensor.fromGpuBuffer(buf, { dataType: dtype, dims });
    this.latentT     = wrap(this.latentBuf,        [1, a.L, D]);
    this.cameraT     = wrap(this.cameraBuf,        [1, 1, C]);
    this.feat1T      = wrap(this.feat1Buf,         [1, a.condL, a.feat1Ch]);
    this.feat2T      = wrap(this.feat2Buf,         [1, a.condL, a.feat2Ch]);
    this.negFeat1T   = wrap(this.negFeat1Buf,      [1, a.condL, a.feat1Ch]);
    this.negFeat2T   = wrap(this.negFeat2Buf,      [1, a.condL, a.feat2Ch]);
    this.condFetches = {
      latent_out: wrap(this.condLatentOutBuf, [1, a.L, D]),
      camera_out: wrap(this.condCameraOutBuf, [1, 1, C]),
    };
    if (a.useCfg) {
      this.uncondFetches = {
        latent_out: wrap(this.uncondLatentOutBuf!, [1, a.L, D]),
        camera_out: wrap(this.uncondCameraOutBuf!, [1, 1, C]),
      };
    }

    const code = this.isFp16 ? EULER_WGSL_FP16 : EULER_WGSL_FP32;
    const module = this.dev.createShaderModule({ code, label: "euler" });
    this.pipeline = await this.dev.createComputePipelineAsync({
      layout: "auto",
      compute: { module, entryPoint: "main" },
    });

    const layout = this.pipeline.getBindGroupLayout(0);
    const bind = (target: GPUBuffer, cond: GPUBuffer, uncond: GPUBuffer) =>
      this.dev.createBindGroup({
        layout,
        entries: [
          { binding: 0, resource: { buffer: target } },
          { binding: 1, resource: { buffer: cond } },
          { binding: 2, resource: { buffer: uncond } },
          { binding: 3, resource: { buffer: this.paramsBuf } },
        ],
      });
    // No-CFG: bind cond into the uncond slot too; shader's select(... cfg>0.5)
    // ignores it. Avoids needing a separate kernel.
    this.latentBindOff = bind(this.latentBuf, this.condLatentOutBuf, this.condLatentOutBuf);
    this.cameraBindOff = bind(this.cameraBuf, this.condCameraOutBuf, this.condCameraOutBuf);
    if (a.useCfg) {
      this.latentBindOn = bind(this.latentBuf, this.condLatentOutBuf, this.uncondLatentOutBuf!);
      this.cameraBindOn = bind(this.cameraBuf, this.condCameraOutBuf, this.uncondCameraOutBuf!);
    }
  }

  /** One Euler step. `tValue` is the same `t` in [0, 1] used elsewhere;
   *  it's scaled to [0, 1000] before being handed to the flow model. */
  async step(tValue: number, dt: number, sScale: number, useCfg: boolean) {
    if (this.destroyed) throw new Error("GpuFlowLoop already destroyed");
    // Build `t` in whatever precision the loaded flow expects. _manual_fp16_cast
    // in quantize.py casts ALL graph inputs (incl. the scalar t) to fp16 when
    // it casts the model; if we keep feeding fp32 here ORT rejects with
    // "Unexpected input data type. Actual: (tensor(float)) , expected:
    // (tensor(float16))" before the run starts. latent / camera / feat1 / feat2
    // already pick up the right dtype via the wrap() call in init().
    const tFp32 = new Float32Array([1000 * tValue]);
    const tTensor = this.isFp16
      ? new ort.Tensor("float16", fp32ToFp16(tFp32), [1])
      : new ort.Tensor("float32", tFp32, [1]);

    const condFeeds = {
      latent: this.latentT, camera: this.cameraT, t: tTensor,
      feat1: this.feat1T, feat2: this.feat2T,
    };
    // Serial: ORT-Web's WASM backend is single-threaded; two
    // session.run calls in flight at once crash with "Session already
    // started" and leave the session in a state where downstream
    // tensor binding fails with "Session mismatch". Same fix we
    // applied to the CPU flow loop and the encode block.
    const condOut: any = await this.flow.run(condFeeds, this.condFetches);
    if (useCfg) {
      const uncondFeeds = {
        latent: this.latentT, camera: this.cameraT, t: tTensor,
        feat1: this.negFeat1T, feat2: this.negFeat2T,
      };
      await this.flow.run(uncondFeeds, this.uncondFetches!);
    }

    if (this.stepCount === 0) await this.verifyOrtHonoredFetches(condOut);
    this.stepCount++;

    // Push the Euler params for this step. dt/s/cfg are shared by the latent
    // and camera dispatches, so we write once and dispatch both passes in
    // the same command encoder.
    const params = new ArrayBuffer(PARAMS_BYTES);
    const f32 = new Float32Array(params);
    f32[0] = dt; f32[1] = sScale; f32[2] = useCfg ? 1.0 : 0.0; f32[3] = 0.0;
    this.dev.queue.writeBuffer(this.paramsBuf, 0, params);

    const enc = this.dev.createCommandEncoder({ label: "euler-step" });
    const pass = enc.beginComputePass();
    pass.setPipeline(this.pipeline);

    // latent: ceil(elements / WG_SIZE) workgroups
    // elements = L*D (fp32) or L*D/2 (fp16-packed u32)
    const elemsPerThread = this.isFp16 ? 2 : 1;
    const dispatch = (elemCount: number) =>
      Math.ceil(elemCount / elemsPerThread / WG_SIZE);

    pass.setBindGroup(0, useCfg ? this.latentBindOn! : this.latentBindOff);
    pass.dispatchWorkgroups(dispatch(this.L * D));
    pass.setBindGroup(0, useCfg ? this.cameraBindOn! : this.cameraBindOff);
    pass.dispatchWorkgroups(dispatch(C));
    pass.end();
    this.dev.queue.submit([enc.finish()]);
  }

  /** Read the final latent back to a CPU Float32 tensor, ready for the
   *  octree + GS decode steps that still live in the existing CPU path. */
  async finalizeLatent(): Promise<ort.Tensor> {
    if (this.destroyed) throw new Error("GpuFlowLoop already destroyed");
    const elements = this.L * D;
    const elemBytes = this.isFp16 ? 2 : 4;
    const byteLen = Math.ceil(elements * elemBytes / 16) * 16;
    const staging = this.dev.createBuffer({
      label: "latent-staging",
      size: byteLen,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    });
    const enc = this.dev.createCommandEncoder({ label: "latent-readback" });
    enc.copyBufferToBuffer(this.latentBuf, 0, staging, 0, byteLen);
    this.dev.queue.submit([enc.finish()]);
    await staging.mapAsync(GPUMapMode.READ);
    const range = staging.getMappedRange(0, byteLen);

    let cpuData: Float32Array;
    if (this.isFp16) {
      const fp16 = new Uint16Array(range.slice(0, elements * 2));
      cpuData = fp16ToFp32(fp16);
    } else {
      // copy out of the mapped range before unmapping
      cpuData = new Float32Array(new Float32Array(range, 0, elements));
    }
    staging.unmap();
    staging.destroy();
    return new ort.Tensor("float32", cpuData, [1, this.L, D]);
  }

  /** After the first flow run, confirm ORT actually wrote into the GPU
   *  buffers we pre-allocated. If `out.latent_out.gpuBuffer` is a different
   *  buffer object, ORT silently ignored our `fetches` and our Euler kernel
   *  would be reading from condLatentOutBuf which never got written. We log
   *  both that fact and a stats peek at our buffer so the empty/garbage
   *  state is unambiguous in the console. */
  private async verifyOrtHonoredFetches(out: any) {
    const expected = this.condLatentOutBuf;
    const got = out?.latent_out?.gpuBuffer;
    const honored = got === expected;
    if (!honored) {
      console.warn(
        "[gpu-loop] ORT did NOT honor pre-allocated fetches: returned a " +
        "different GPUBuffer for latent_out. Pre-allocated IO binding " +
        "isn't working with this ORT-Web build; falling back to CPU path " +
        "is recommended.",
        { expected, got, location: out?.latent_out?.location });
    } else {
      console.log("[gpu-loop] ORT honored pre-allocated fetches (latent_out -> " +
                  "our condLatentOutBuf). Reading back to verify it's non-empty...");
    }
    // Read back regardless so we can confirm the bytes look like a flow
    // velocity field (~N(0, ~1) with the model's actual scale), not zeros
    // or NaN.
    const elements = this.L * D;
    const elemBytes = this.isFp16 ? 2 : 4;
    const byteLen = Math.ceil(elements * elemBytes / 16) * 16;
    const staging = this.dev.createBuffer({
      size: byteLen,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      label: "peek-condLatentOut",
    });
    const enc = this.dev.createCommandEncoder({ label: "peek-cond" });
    enc.copyBufferToBuffer(this.condLatentOutBuf, 0, staging, 0, byteLen);
    this.dev.queue.submit([enc.finish()]);
    await staging.mapAsync(GPUMapMode.READ);
    const range = staging.getMappedRange(0, byteLen);
    const cpu = this.isFp16
      ? fp16ToFp32(new Uint16Array(range.slice(0, elements * 2)))
      : new Float32Array(new Float32Array(range, 0, elements));
    staging.unmap();
    staging.destroy();
    const n = cpu.length;
    let nz = 0, nans = 0;
    let min = Infinity, max = -Infinity, sum = 0;
    for (let i = 0; i < n; i++) {
      const v = cpu[i];
      if (Number.isNaN(v)) { nans++; continue; }
      if (v !== 0) nz++;
      if (v < min) min = v;
      if (v > max) max = v;
      sum += v;
    }
    const mean = sum / Math.max(1, n - nans);
    const verdict = nz === 0 ? "ALL ZERO -- ORT did not write to it"
                  : nans > n / 4 ? "MOSTLY NaN -- the model produced bad output"
                  : "looks populated";
    console.log(`[gpu-loop] condLatentOutBuf after step 0: nz=${nz}/${n} ` +
                `nan=${nans} min=${min.toFixed(3)} max=${max.toFixed(3)} ` +
                `mean=${mean.toFixed(4)} -- ${verdict}`);
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const buf of [
      this.latentBuf, this.cameraBuf, this.feat1Buf, this.feat2Buf,
      this.negFeat1Buf, this.negFeat2Buf,
      this.condLatentOutBuf, this.condCameraOutBuf,
      this.uncondLatentOutBuf, this.uncondCameraOutBuf,
      this.paramsBuf,
    ]) {
      if (buf) buf.destroy();
    }
  }

  private writeBuf(buf: GPUBuffer, src: Float32Array, byteLen: number) {
    // queue.writeBuffer demands the data size be a multiple of 4 bytes, so
    // we copy into a fresh ArrayBuffer sized to byteLen (which init() already
    // rounded up). The fresh buffer is also strictly an ArrayBuffer (not
    // SharedArrayBuffer), which keeps TypeScript's BufferSource union happy.
    if (this.isFp16) {
      const fp16 = fp32ToFp16(src);
      const ab = new ArrayBuffer(byteLen);
      new Uint8Array(ab).set(new Uint8Array(fp16.buffer, fp16.byteOffset, fp16.byteLength));
      this.dev.queue.writeBuffer(buf, 0, ab);
    } else {
      const ab = new ArrayBuffer(byteLen);
      new Uint8Array(ab).set(new Uint8Array(src.buffer, src.byteOffset, src.byteLength));
      this.dev.queue.writeBuffer(buf, 0, ab);
    }
  }
}

// ---------------------------------------------------------------------------
// WGSL kernels
// ---------------------------------------------------------------------------

const EULER_WGSL_FP32 = /* wgsl */ `
struct Params { dt: f32, s: f32, cfg: f32, _pad: f32, };
@group(0) @binding(0) var<storage, read_write> latent: array<f32>;
@group(0) @binding(1) var<storage, read>       cond:   array<f32>;
@group(0) @binding(2) var<storage, read>       uncond: array<f32>;
@group(0) @binding(3) var<uniform>             p: Params;

@compute @workgroup_size(${WG_SIZE})
fn main(@builtin(global_invocation_id) gid: vec3u) {
  let i = gid.x;
  let n = arrayLength(&latent);
  if (i >= n) { return; }
  let c = cond[i];
  let v = select(c, p.s * c - (p.s - 1.0) * uncond[i], p.cfg > 0.5);
  latent[i] = latent[i] - p.dt * v;
}
`;

// fp16 path: each array element is a u32 holding two packed fp16 values.
// pack2x16float / unpack2x16float are core WGSL -- no shader-f16 feature.
const EULER_WGSL_FP16 = /* wgsl */ `
struct Params { dt: f32, s: f32, cfg: f32, _pad: f32, };
@group(0) @binding(0) var<storage, read_write> latent: array<u32>;
@group(0) @binding(1) var<storage, read>       cond:   array<u32>;
@group(0) @binding(2) var<storage, read>       uncond: array<u32>;
@group(0) @binding(3) var<uniform>             p: Params;

@compute @workgroup_size(${WG_SIZE})
fn main(@builtin(global_invocation_id) gid: vec3u) {
  let i = gid.x;
  let n = arrayLength(&latent);
  if (i >= n) { return; }
  let l = unpack2x16float(latent[i]);
  let c = unpack2x16float(cond[i]);
  let u = unpack2x16float(uncond[i]);
  let v = select(c, p.s * c - vec2f(p.s - 1.0) * u, p.cfg > 0.5);
  latent[i] = pack2x16float(l - vec2f(p.dt) * v);
}
`;
